import { AlertTriangle, ArrowUpRight, Wrench } from 'lucide-react';
import type { FleetInsightsDoc } from '../../services/fleetClientService';

interface Modernization {
  task: string;
  label: string;
  legacy_name: string;
  llm_free: boolean;
  candidate_repos: string[];
}

function ModRow({
  mod,
  onPush,
}: {
  mod: Modernization;
  onPush?: (taskType: string, label: string) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm text-gray-900 dark:text-gray-200">{mod.label}</div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {mod.candidate_repos.length} repos still on {mod.legacy_name}
        </p>
      </div>
      {onPush && (
        <button
          onClick={() => onPush(mod.task, mod.label)}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-600 text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow active:scale-[.97]"
        >
          <Wrench className="w-3.5 h-3.5" />
          Push to {mod.candidate_repos.length} repos
        </button>
      )}
    </li>
  );
}

export function Modernizations({
  insights,
  onPush,
}: {
  insights: FleetInsightsDoc;
  onPush?: (taskType: string, label: string) => void;
}) {
  const mods = (insights.modernizations ?? []).filter((m) => m.candidate_repos.length > 0);
  if (mods.length === 0) return null;
  const pushable = mods.filter((m) => m.llm_free);
  const assisted = mods.filter((m) => !m.llm_free);

  return (
    <section className="p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/30">
          <ArrowUpRight className="w-4 h-4 text-teal-600 dark:text-teal-400" />
        </span>
        <h2 className="font-semibold text-gray-900 dark:text-white">Modernization opportunities</h2>
      </div>

      {pushable.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-1.5 mb-2">
            <Wrench className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
            <h3 className="text-xs font-medium text-teal-700 dark:text-teal-400 uppercase tracking-wide">
              Pushable now — deterministic migration
            </h3>
          </div>
          <ul className="space-y-3">
            {pushable.map((m) => (
              <ModRow key={m.task} mod={m} onPush={onPush} />
            ))}
          </ul>
        </div>
      )}

      {assisted.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            LLM-assisted — needs review before rollout
          </h3>
          <ul className="space-y-3">
            {assisted.map((m) => (
              <ModRow key={m.task} mod={m} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

const SEVERITY_TONE: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

interface SystemicIssue {
  entity_slug: string;
  title: string;
  severity: string;
  affected_count: number;
}

function IssueRow({
  issue,
  onFix,
}: {
  issue: SystemicIssue;
  onFix?: (entitySlug: string, issueTitle: string) => void;
}) {
  return (
    <li className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${SEVERITY_TONE[issue.severity] ?? SEVERITY_TONE.low}`}
          >
            {issue.severity}
          </span>
          <span className="text-sm text-gray-900 dark:text-gray-200 truncate">{issue.title}</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {issue.affected_count} repositories affected
        </p>
      </div>
      {onFix && (
        <button
          onClick={() => onFix(issue.entity_slug, issue.title)}
          className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-600 text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow active:scale-[.97]"
        >
          <Wrench className="w-3.5 h-3.5" />
          Fix across {issue.affected_count} repos
        </button>
      )}
    </li>
  );
}

export function SystemicIssues({
  insights,
  onFix,
  fixableSlugs,
}: {
  insights: FleetInsightsDoc;
  onFix?: (entitySlug: string, issueTitle: string) => void;
  fixableSlugs?: Set<string>;
}) {
  const fixable: SystemicIssue[] = [];
  const review: SystemicIssue[] = [];
  for (const issue of insights.systemic_issues) {
    (fixableSlugs?.has(issue.entity_slug) ? fixable : review).push(issue);
  }

  return (
    <section className="p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
          <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
        </span>
        <h2 className="font-semibold text-gray-900 dark:text-white">Systemic issues</h2>
      </div>

      {fixable.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-1.5 mb-2">
            <Wrench className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
            <h3 className="text-xs font-medium text-teal-700 dark:text-teal-400 uppercase tracking-wide">
              Fixable now — deterministic, one click
            </h3>
          </div>
          <ul className="space-y-3">
            {fixable.map((issue) => (
              <IssueRow key={`${issue.entity_slug}-${issue.title}`} issue={issue} onFix={onFix} />
            ))}
          </ul>
        </div>
      )}

      {review.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Needs review — no automated fix yet
          </h3>
          <ul className="space-y-3">
            {review.slice(0, 8).map((issue) => (
              <IssueRow key={`${issue.entity_slug}-${issue.title}`} issue={issue} />
            ))}
          </ul>
          {review.length > 8 && (
            <p className="text-xs text-gray-400 mt-2">+{review.length - 8} more advisory issues</p>
          )}
        </div>
      )}
    </section>
  );
}
